<script setup lang="ts">
import { inject, computed, ref, watch } from 'vue'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { useFormsStore } from '@/stores/forms'
import { useSyncStore } from '@/stores/sync'
import FormShell from '@/components/forms/FormShell.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'
import FosButton from '@/components/ui/FosButton.vue'
import { FormType, OverheadRole } from '@/types'

const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const formsStore = useFormsStore()
const syncStore = useSyncStore()
const incidentId = inject<string>('incidentId', '')

const FORM_TYPE = FormType.ICS214

const formData = computed(() => formsStore.getFormData(FORM_TYPE))
const incident = computed(() => incidentStore.currentIncident)
const op = computed(() => opsStore.selectedOp)

function namesForRole(role: string): string {
  return teamsStore.userOps
    .filter((uo) => uo.role === role && uo.active)
    .map((uo) => personnelStore.roster.find((p) => p.personnel_id === uo.personnel_id))
    .filter((p) => p !== undefined)
    .map((p) => `${p.last}, ${p.first}`)
    .join('; ')
}

const icName = computed(() => namesForRole(OverheadRole.IC) || '—')
const opsChief = computed(() => namesForRole(OverheadRole.OpsChief) || '—')
const plansChief = computed(() => namesForRole(OverheadRole.PlansChief) || '—')
const logistics = computed(() => namesForRole(OverheadRole.Logistics) || '—')

function field(key: string): string {
  const v = formData.value[key]
  return v != null ? String(v) : ''
}

async function save(fieldPath: string, event: Event) {
  if (!incidentId) return
  const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, FORM_TYPE, fieldPath, value)
  } finally {
    syncStore.pendingWrites--
  }
}

// Activity log entries
interface ActivityEntry {
  id: string
  time: string
  notable_activities: string
  major_events: string
}

const entries = ref<ActivityEntry[]>([])

const storeEntries = computed(
  () => (formData.value.entries as ActivityEntry[] | undefined) ?? [],
)

watch(
  storeEntries,
  (incoming) => {
    const incomingIds = incoming.map((e) => e.id).join(',')
    const localIds = entries.value.map((e) => e.id).join(',')
    if (incomingIds !== localIds) entries.value = incoming.map((e) => ({ ...e }))
  },
  { immediate: true },
)

async function saveEntries() {
  if (!incidentId) return
  syncStore.pendingWrites++
  try {
    await formsStore.saveFormField(incidentId, FORM_TYPE, 'entries', entries.value)
  } finally {
    syncStore.pendingWrites--
  }
}

function addEntry() {
  entries.value.push({ id: crypto.randomUUID(), time: '', notable_activities: '', major_events: '' })
  saveEntries()
}

function onEntryBlur(entryId: string, fieldKey: keyof ActivityEntry, event: Event) {
  const entry = entries.value.find((e) => e.id === entryId)
  if (entry) {
    ;(entry as Record<string, string>)[fieldKey] = (event.target as HTMLInputElement | HTMLTextAreaElement).value
    saveEntries()
  }
}

function removeEntry(id: string) {
  entries.value = entries.value.filter((e) => e.id !== id)
  saveEntries()
}

const sortedEntries = computed(() =>
  [...entries.value].sort((a, b) => {
    if (!a.time) return 1
    if (!b.time) return -1
    return a.time.localeCompare(b.time)
  }),
)
</script>

<template>
  <FormShell
    :form-type="FORM_TYPE"
    title="ICS 214 · Activity Log"
    back-route="incident-forms"
    back-label="Forms"
    :back-params="{ id: incidentId }"
  >
    <div class="max-w-[900px] mx-auto px-7 py-[22px] pb-16 flex flex-col gap-[18px]">
      <!-- Section head -->
      <div class="flex items-end gap-3.5 flex-wrap">
        <div class="flex flex-col gap-0.5">
          <span class="font-mono text-[11px] tracking-[0.12em] uppercase text-primary">ICS 214 · Activity Log</span>
          <h2 class="font-headline font-black text-[26px] uppercase tracking-tight text-on-surface m-0">Activity Log</h2>
        </div>
        <p class="flex-1 min-w-[200px] text-[13px] text-outline m-0">Log of notable activities and major events for this operational period.</p>
      </div>

      <!-- Incident / OP (read-only) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Incident / Operational Period</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Read-only</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Name</label>
            <p class="font-body text-[13px] text-on-surface">{{ incident?.incident_name ?? '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operational Period</label>
            <p class="font-mono text-[13px] text-on-surface">{{ op ? `OP ${op.op_number}` : '—' }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">OP Date</label>
            <p class="font-body text-[13px] text-on-surface">{{ op?.date ?? '—' }}</p>
          </div>
        </div>
      </div>

      <!-- Unit Name -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Unit / Section Name</h3>
        </div>
        <div class="p-3.5">
          <FosInput
            :model-value="field('unit_name')"
            placeholder="Unit or section name (e.g. Operations, Logistics)…"
            @blur="save('unit_name', $event)"
          />
        </div>
      </div>

      <!-- Command Staff (read-only, live from roster) -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Command Staff</h3>
          <span class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline px-1.5 py-0.5 border border-border rounded bg-surface-container-low ml-auto">Live from roster</span>
        </div>
        <div class="p-3.5 grid grid-cols-2 gap-3">
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Incident Commander</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ icName }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Operations Section Chief</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ opsChief }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Plans Section Chief</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ plansChief }}</p>
          </div>
          <div>
            <label class="font-mono text-[10px] tracking-[0.10em] uppercase text-outline block mb-1">Logistics Section Chief</label>
            <p class="font-body text-[13px] text-on-surface px-2.5 py-2 bg-surface-container-low border border-border rounded">{{ logistics }}</p>
          </div>
        </div>
      </div>

      <!-- Activity Log Entries -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Activity Log</h3>
          <span class="font-mono text-[11px] text-outline ml-auto">{{ entries.length }} entries</span>
        </div>

        <div class="flex flex-col divide-y divide-border/40">
          <div
            v-for="entry in sortedEntries"
            :key="entry.id"
            class="grid gap-2.5 p-3.5 group items-start"
            style="grid-template-columns: 100px 1fr 1fr auto;"
          >
            <div>
              <label class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline block mb-1">Time</label>
              <FosInput
                type="time"
                :model-value="entry.time"
                @blur="onEntryBlur(entry.id, 'time', $event)"
              />
            </div>
            <div>
              <label class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline block mb-1">Notable Activities</label>
              <FosTextarea
                :model-value="entry.notable_activities"
                placeholder="Activity description…"
                :rows="2"
                @blur="onEntryBlur(entry.id, 'notable_activities', $event)"
              />
            </div>
            <div>
              <label class="font-mono text-[9px] tracking-[0.10em] uppercase text-outline block mb-1">Major Events</label>
              <FosTextarea
                :model-value="entry.major_events"
                placeholder="Significant events…"
                :rows="2"
                @blur="onEntryBlur(entry.id, 'major_events', $event)"
              />
            </div>
            <button
              class="mt-5 w-6 h-6 inline-flex items-center justify-center text-outline hover:text-error border border-transparent rounded hover:border-border hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
              @click="removeEntry(entry.id)"
            >
              <span class="material-symbols-outlined text-[16px]">close</span>
            </button>
          </div>

          <div v-if="entries.length === 0" class="p-6 text-center">
            <p class="font-mono text-[12px] text-outline">No activity entries yet.</p>
          </div>
        </div>

        <div class="px-3.5 py-2.5 border-t border-border/60">
          <FosButton variant="secondary" size="sm" @click="addEntry">
            <span class="material-symbols-outlined text-[14px] mr-1">add</span>
            Add Entry
          </FosButton>
        </div>
      </div>

      <!-- Prepared By -->
      <div class="bg-surface border border-border rounded flex flex-col">
        <div class="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
          <h3 class="font-mono font-semibold text-[11px] tracking-[0.12em] uppercase text-on-surface m-0">Prepared By</h3>
        </div>
        <div class="p-3.5">
          <FosInput
            :model-value="field('prepared_by')"
            placeholder="Name of person preparing ICS 214…"
            @blur="save('prepared_by', $event)"
          />
        </div>
      </div>
    </div>
  </FormShell>
</template>
